import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserProfile from './UserProfile';
import LanguageSelector from './LanguageSelector';
import './Header.css';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('Claro');

  // Load theme preference on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme-preference') || 'Claro';
    setCurrentTheme(savedTheme);
    
    // Apply saved theme
    const root = document.documentElement;
    if (savedTheme === 'Escuro') {
      root.setAttribute('data-theme', 'dark');
    } else {
      // Claro theme - explicitly set light theme
      root.setAttribute('data-theme', 'light');
    }
  }, []);

  const handleUserProfileClick = () => {
    setShowEmailInput(true);
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userEmail.trim()) {
      setShowUserProfile(true);
      setShowEmailInput(false);
    }
  };

  const handleThemeChange = (theme: string) => {
    setCurrentTheme(theme);
    
    // Apply theme to document
    const root = document.documentElement;
    
    if (theme === 'Escuro') {
      root.setAttribute('data-theme', 'dark');
    } else {
      // Claro theme - explicitly set light theme
      root.setAttribute('data-theme', 'light');
    }
    
    // Save preference to localStorage
    localStorage.setItem('theme-preference', theme);
  };

  const handleShare = () => {
    // Implementar lógica de compartilhamento
    console.log('Share clicked');
  };

  return (
    <>
      <header className="header-modern">
        <div className="header-container">
          {/* Logo Section */}
          <div className="logo-section">
            <Link to="/" className="logo-modern">
              <div className="logo-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect width="32" height="32" rx="8" fill="#6366f1"/>
                  <path d="M16 8L20 12H12L16 8Z" fill="white"/>
                  <rect x="10" y="14" width="12" height="2" fill="white"/>
                  <rect x="8" y="18" width="16" height="2" fill="white"/>
                  <rect x="10" y="22" width="12" height="2" fill="white"/>
                </svg>
              </div>
              <span className="logo-text">EventHub</span>
            </Link>
          </div>

          {/* Navigation Links */}
          <nav className="nav-links">
            <Link to="/" className="nav-item">{t('header.events')}</Link>
            <button className="nav-item nav-button" onClick={handleUserProfileClick}>
              {t('header.profile')}
            </button>
          </nav>

          {/* Right Section - Language, Theme Controls & User */}
          <div className="header-controls">
            {/* Language Selector */}
            <LanguageSelector showLabel={false} className="header-language" />

            {/* Theme Selector */}
            <div className="theme-controls">
              <button 
                className={`theme-btn ${currentTheme === 'Claro' ? 'active' : ''}`}
                onClick={() => handleThemeChange('Claro')}
              >
                ☀️ Claro
              </button>
              <button 
                className={`theme-btn ${currentTheme === 'Escuro' ? 'active' : ''}`}
                onClick={() => handleThemeChange('Escuro')}
              >
                🌙 Escuro
              </button>
            </div>

            {/* Share Button */}
            <button className="share-btn" onClick={handleShare}>
              🔗 {t('header.share')}
            </button>

            {/* User Avatar */}
            <button 
              className="user-avatar" 
              onClick={handleUserProfileClick}
              title={t('header.profile')}
            >
              <div className="avatar-circle">
                <span>U</span>
              </div>
            </button>
          </div>
        </div>
      </header>

      {showEmailInput && (
        <div className="modal-overlay" onClick={() => setShowEmailInput(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Access Profile</h2>
              <button className="close-btn" onClick={() => setShowEmailInput(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleEmailSubmit}>
                <div className="form-group">
                  <label htmlFor="profile-email">Enter your email to access your profile:</label>
                  <input
                    id="profile-email"
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    required
                    placeholder="Enter your email address"
                    autoFocus
                  />
                </div>
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowEmailInput(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={!userEmail.trim()}
                  >
                    View Profile
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showUserProfile && (
        <UserProfile 
          userEmail={userEmail}
          onClose={() => {
            setShowUserProfile(false);
            setUserEmail('');
          }}
        />
      )}
    </>
  );
};

export default Header;
